// Build customer profile context string for agent prompts
// Loads customer, entity, and user data into a structured block
// that the Issue Recorder agent uses to avoid re-asking known information

import type { Customer, CustomerEntity, CustomerUser } from '@/types/customer'

/**
 * Build the full customer profile context block for the agent prompt.
 * This gives the agent all known environment info so it never asks for basics.
 */
export function buildCustomerProfileContext(
  customer: Customer | null,
  entity: CustomerEntity | null,
  reportedBy: CustomerUser | null,
): string {
  if (!customer) return ''

  const sections: string[] = ['[CUSTOMER PROFILE]']

  // Customer basics
  sections.push(`Customer: ${customer.name}`)
  if (customer.company_code) sections.push(`Company Code: ${customer.company_code}`)
  if (customer.industry) sections.push(`Industry: ${customer.industry}`)
  if (customer.country) sections.push(`Country: ${customer.country}`)
  if (customer.contact_email) sections.push(`Contact Email: ${customer.contact_email}`)

  // Entity / environment details
  if (entity) {
    sections.push('')
    sections.push('[ENVIRONMENT]')
    sections.push(`Entity: ${entity.name}`)
    if (entity.d365_version) sections.push(`D365 Version: ${entity.d365_version}`)
    if (entity.d365_products?.length) {
      sections.push(`D365 Products: ${entity.d365_products.join(', ')}`)
    }
    if (entity.d365_deployment_type) {
      sections.push(`Deployment Type: ${formatDeployment(entity.d365_deployment_type)}`)
    }
    if (entity.environment_type) {
      sections.push(`Environment Type: ${entity.environment_type}`)
    }
    if (entity.lcs_project_id) sections.push(`LCS Project ID: ${entity.lcs_project_id}`)
    if (entity.environment_url) sections.push(`Environment URL: ${entity.environment_url}`)
    if (entity.description) sections.push(`Environment Notes: ${entity.description}`)
  }

  // Configuration notes
  if (customer.configuration_notes) {
    sections.push('')
    sections.push(`[CONFIGURATION NOTES]`)
    sections.push(customer.configuration_notes)
  }

  // Agent instructions (account-manager-defined rules)
  if (customer.agent_instructions) {
    sections.push('')
    sections.push(`[AGENT INSTRUCTIONS]`)
    sections.push(customer.agent_instructions)
  }

  // Customer description
  if (customer.description) {
    sections.push('')
    sections.push(`[CUSTOMER DESCRIPTION]`)
    sections.push(customer.description)
  }

  // Reported by
  if (reportedBy) {
    sections.push('')
    sections.push('[REPORTED BY]')
    sections.push(`Name: ${reportedBy.name}`)
    if (reportedBy.job_title) sections.push(`Role: ${reportedBy.job_title}`)
    if (reportedBy.department) sections.push(`Department: ${reportedBy.department}`)
    if (reportedBy.email) sections.push(`Email: ${reportedBy.email}`)
  }

  sections.push('[END CUSTOMER PROFILE]')
  return sections.join('\n')
}

function formatDeployment(type: string): string {
  const map: Record<string, string> = {
    cloud: 'Cloud (Microsoft-managed)',
    on_premises: 'On-Premises',
    cloud_hosted_dev: 'Cloud-Hosted Dev/Test',
  }
  return map[type] || type
}
